import { useEffect, useMemo, useState } from "react";
import {
  resolveCuratorProfilePath,
  writeCachedCuratorUsername,
} from "../shared/curatorUserFilmsProfile";
import { fetchAppleApi, fetchJsonOrThrow, REQUEST_HEADERS } from "./appleTvApi";

export default function useCurrentCuratorProfilePath({
  isAuthenticated,
  user,
  fallbackPath = "/profile",
} = {}) {
  const userUsername = user?.username;
  const userNickname = user?.nickname;
  const userPreferredUsername = user?.preferred_username;
  const userSub = user?.sub;
  const userId = user?.user_id;
  const userEmail = user?.email;
  const userForPath = useMemo(() => ({
    username: userUsername,
    nickname: userNickname,
    preferred_username: userPreferredUsername,
  }), [userNickname, userPreferredUsername, userUsername]);
  const authUserId = useMemo(
    () => String(userSub || userId || userEmail || userNickname || "").trim(),
    [userEmail, userId, userNickname, userSub],
  );
  const optimisticPath = useMemo(
    () => resolveCuratorProfilePath({ user: userForPath, fallbackPath }),
    [fallbackPath, userForPath],
  );
  const [profilePath, setProfilePath] = useState(optimisticPath);

  useEffect(() => {
    setProfilePath(optimisticPath);
    if (!isAuthenticated) return undefined;

    if (!authUserId) return undefined;

    let cancelled = false;
    const headers = {
      ...REQUEST_HEADERS,
      "user-id": authUserId,
    };

    async function resolvePath() {
      try {
        const meResponse = await fetchAppleApi("/api/v1/users/me", { headers });
        const profile = await fetchJsonOrThrow(meResponse, "profile");
        if (cancelled) return;
        const next = resolveCuratorProfilePath({ profile, user: userForPath, fallbackPath });
        writeCachedCuratorUsername(profile?.username || profile?.curator_username);
        setProfilePath(next);
        return;
      } catch (_meErr) {
        // Fall through to Letterboxd profile username.
      }
      try {
        const lbResponse = await fetchAppleApi(
          `/api/letterboxd/profile?userId=${encodeURIComponent(authUserId)}`,
          { headers },
        );
        const letterboxd = await fetchJsonOrThrow(lbResponse, "Letterboxd profile");
        if (cancelled) return;
        const lbUsername = letterboxd?.letterboxdUsername
          || letterboxd?.profile?.username
          || letterboxd?.username;
        if (lbUsername) {
          writeCachedCuratorUsername(lbUsername);
          setProfilePath(resolveCuratorProfilePath({
            profile: { username: lbUsername },
            user: userForPath,
            fallbackPath,
          }));
          return;
        }
      } catch (_lbErr) {
        // keep optimistic
      }
      if (!cancelled) setProfilePath(optimisticPath);
    }

    resolvePath();

    return () => {
      cancelled = true;
    };
  }, [authUserId, fallbackPath, isAuthenticated, optimisticPath, userForPath]);

  return profilePath;
}
